import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import bookOpenFill from "@iconify/icons-eva/book-open-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import trash2Fill from "@iconify/icons-eva/trash-2-fill";
import Iconify from "src/components/iconify";
import PropTypes from "prop-types";
import { clientesApi } from "src/services/clientesApi";
import { tiposApi } from "src/services/tiposApi";
import { useSnackbar } from "src/components/snackbar";
import VoiceInputButton from "src/components/propuesta/VoiceInputButton";
import AICorrectButton from "src/components/propuesta/AICorrectButton";
import { obtenerMeses } from "src/utils/formatTime";
import { calcValorConDescuento } from "src/utils/propuestasV2/calcValorConDescuento";
import { BRAND } from "src/theme/brandTokens";
import { strToDate, dateToStr } from "src/utils/propuestasV2/fechasIso";
import { normalizarRut } from "src/utils/propuestasV2/normalizarRut";

const CATALOG_BG = "#F0F7FF";
const MONEDAS = ["CLP", "UF", "USD", "EUR"];

// Usar obtenerMeses() del proyecto — evita duplicación
const MESES = obtenerMeses().map((nombre, i) => ({
  value: String(i + 1).padStart(2, "0"),
  label: nombre.charAt(0) + nombre.slice(1).toLowerCase(),
}));

/** Convierte nombres de mes en español (MARZO) o números sin cero (3) a "01"–"12" */
const normalizarMes = (m) => {
  if (!m) return "";
  const upper = String(m).toUpperCase().trim();
  const idx = obtenerMeses().indexOf(upper);
  if (idx !== -1) return String(idx + 1).padStart(2, "0");
  const n = Number(m);
  if (!Number.isNaN(n) && n >= 1 && n <= 12) return String(n).padStart(2, "0");
  return m; // ya está en formato correcto ("01"–"12")
};

const MONEDA_MAP = { PESOS: "CLP", DOLAR: "USD", EURO: "EUR", DOLARES: "USD" };
const normalizarMoneda = (m) =>
  MONEDA_MAP[m] ||
  (MONEDAS.some((x) => x.value === m || m === x.label)
    ? m
    : MONEDAS.find((x) => x.value === m || m === x.value)?.value ||
      (["CLP", "UF", "USD", "EUR"].includes(m) ? m : "CLP"));

const CLIENTE_NUEVO_INICIAL = {
  nombre: "",
  rut: "",
  correo: "",
  telefono: "",
  direccion: "",
  comuna: "",
};

// Una sola constante para los argumentos de la consulta de clientes: el selector
// lee esa entrada de la caché y la creación escribe en ella. Con dos literales
// separados, cambiar uno dejaría la escritura apuntando a otra entrada y el
// cliente nuevo no aparecería, sin que nada falle de forma visible.
const CLIENTES_QUERY_ARGS = { estado: "TODOS" };

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Dialog de edición para una fase/servicio de la propuesta.
 * Props:
 *   isNew — true cuando se está creando una fase nueva (afecta el título del dialog)
 */
const FaseEditDialog = ({
  open,
  onClose,
  item,
  itemIndex,
  onSave,
  onDelete,
  empleados,
  sucursal,
  isNew,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [local, setLocal] = useState({ ...item });
  const [errores, setErrores] = useState({});
  const set = (campo, valor) => {
    setLocal((prev) => ({ ...prev, [campo]: valor }));
    // Limpiar el error del campo apenas el usuario lo edita.
    setErrores((prev) =>
      prev[campo] ? { ...prev, [campo]: undefined } : prev,
    );
  };

  // Catálogo
  const [triggerPlanes, { data: tipoPlanesList = [] }] =
    tiposApi.useLazyGetTipoPlanesQuery();
  const [triggerTareas, { data: tipoTareaList = [] }] =
    tiposApi.useLazyGetTipoTareasQuery();

  // Cargar catálogo una sola vez al montar
  useEffect(() => {
    triggerPlanes();
    triggerTareas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const planesActivos = tipoPlanesList.filter(
    (p) => p.activo === 1 || p.activo === true,
  );
  const tareasActivas = tipoTareaList.filter(
    (t) => t.activo === 1 || t.activo === true,
  );

  // Indicador de campos pre-rellenados desde catálogo
  const [camposCatalogo, setCamposCatalogo] = useState(new Set());
  const marcarCatalogo = (campos) =>
    setCamposCatalogo((prev) => {
      const n = new Set(prev);
      campos.forEach((c) => n.add(c));
      return n;
    });
  const desmarcarCatalogo = (campo) =>
    setCamposCatalogo((prev) => {
      const n = new Set(prev);
      n.delete(campo);
      return n;
    });

  // Enriquecer desde el catálogo cuando el item ya tiene una referencia
  // (p.ej. un BORRADOR generado por IA con `refTipoPlan` pero sin valor ni
  // narrativa). Corre cuando el catálogo se carga por primera vez.
  //
  // El item del estado local manda sobre el catálogo: si un campo ya tiene
  // contenido (porque el usuario lo editó previamente o porque el item se
  // reabrió desde BD), no se sobrescribe. El catálogo solo rellena lo vacío.
  // Eso evita perder ediciones manuales al reabrir el dialog.
  const catalogApplied = useRef(false);
  useEffect(() => {
    if (catalogApplied.current) return;

    if (
      local.tipoItem === "PLAN" &&
      local.refTipoPlan &&
      planesActivos.length > 0
    ) {
      const plan = planesActivos.find((p) => p.nombre === local.refTipoPlan);
      if (plan) {
        catalogApplied.current = true;
        const snap = local;
        const valorOriginal =
          Number(snap.valorOriginal) > 0
            ? snap.valorOriginal
            : (plan.valor ?? 0);
        setLocal((prev) => ({
          ...prev,
          faseTitulo: prev.faseTitulo || plan.nombre || "",
          faseObjetivo:
            prev.faseObjetivo ||
            plan.descripcion_publica ||
            plan.descripcion ||
            "",
          faseActividades: prev.faseActividades || plan.alcance || "",
          faseEntregable: prev.faseEntregable || plan.condiciones || "",
          valorOriginal,
          valorConDescuento: calcValorConDescuento(
            valorOriginal,
            prev.descuento,
          ),
          tipoMoneda:
            prev.tipoMoneda || normalizarMoneda(plan.tipoMoneda) || "",
          tipoFrecuencia:
            prev.tipoFrecuencia || plan.tipoFrecuencia_default || "",
          mesCargaTareas:
            prev.mesCargaTareas ||
            normalizarMes(plan.mesCargaTareas_default) ||
            "",
          mesFacturacion:
            prev.mesFacturacion ||
            normalizarMes(plan.mesFacturacion_default) ||
            "",
        }));
        // Marcar como "desde catálogo" solo los campos que el catálogo
        // efectivamente rellenó (estaban vacíos en el snapshot).
        const campos = [];
        if (!snap.faseTitulo && plan.nombre) campos.push("faseTitulo");
        if (
          !snap.faseObjetivo &&
          (plan.descripcion_publica || plan.descripcion)
        )
          campos.push("faseObjetivo");
        if (!snap.faseActividades && plan.alcance)
          campos.push("faseActividades");
        if (!snap.faseEntregable && plan.condiciones)
          campos.push("faseEntregable");
        if (!(Number(snap.valorOriginal) > 0) && plan.valor != null)
          campos.push("valorOriginal");
        if (!snap.tipoMoneda && plan.tipoMoneda) campos.push("tipoMoneda");
        marcarCatalogo(campos);
      }
    } else if (
      local.tipoItem === "TAREA" &&
      local.refTipoTarea &&
      tareasActivas.length > 0
    ) {
      const tarea = tareasActivas.find((t) => t.nombre === local.refTipoTarea);
      if (tarea) {
        catalogApplied.current = true;
        const snap = local;
        const valorOriginal =
          Number(snap.valorOriginal) > 0
            ? snap.valorOriginal
            : (tarea.valor ?? 0);
        setLocal((prev) => ({
          ...prev,
          faseTitulo: prev.faseTitulo || tarea.nombre || "",
          faseObjetivo:
            prev.faseObjetivo ||
            tarea.objetivo ||
            tarea.descripcion_publica ||
            tarea.descripcion ||
            "",
          faseActividades: prev.faseActividades || tarea.alcance || "",
          faseEntregable: prev.faseEntregable || tarea.entregable || "",
          faseDuracion: prev.faseDuracion || tarea.duracion_estimada || "",
          valorOriginal,
          valorConDescuento: calcValorConDescuento(
            valorOriginal,
            prev.descuento,
          ),
          tipoMoneda:
            prev.tipoMoneda || normalizarMoneda(tarea.tipoMoneda) || "",
        }));
        const campos = [];
        if (!snap.faseTitulo && tarea.nombre) campos.push("faseTitulo");
        if (
          !snap.faseObjetivo &&
          (tarea.objetivo || tarea.descripcion_publica || tarea.descripcion)
        )
          campos.push("faseObjetivo");
        if (!snap.faseActividades && tarea.alcance)
          campos.push("faseActividades");
        if (!snap.faseEntregable && tarea.entregable)
          campos.push("faseEntregable");
        if (!snap.faseDuracion && tarea.duracion_estimada)
          campos.push("faseDuracion");
        if (!(Number(snap.valorOriginal) > 0) && tarea.valor != null)
          campos.push("valorOriginal");
        if (!snap.tipoMoneda && tarea.tipoMoneda) campos.push("tipoMoneda");
        marcarCatalogo(campos);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planesActivos, tareasActivas]);

  // ── Auto-fill desde catálogo ─────────────────────────────────────────────
  const handleSeleccionarPlan = (_, plan) => {
    if (!plan) {
      setLocal((prev) => ({ ...prev, refTipoPlan: "", nombreServicio: "" }));
      setCamposCatalogo(new Set());
      return;
    }
    const campos = [];
    const valorOriginal = plan.valor ?? 0;

    setLocal((prev) => ({
      ...prev,
      refTipoPlan: plan.nombre,
      nombreServicio: plan.nombre,
      faseTitulo: plan.nombre || prev.faseTitulo,
      faseObjetivo:
        plan.descripcion_publica || plan.descripcion || prev.faseObjetivo,
      faseActividades: plan.alcance || prev.faseActividades,
      faseEntregable: plan.condiciones || prev.faseEntregable,
      valorOriginal,
      valorConDescuento: calcValorConDescuento(valorOriginal, prev.descuento),
      tipoMoneda: normalizarMoneda(plan.tipoMoneda) || prev.tipoMoneda,
      tipoFrecuencia: plan.tipoFrecuencia_default || prev.tipoFrecuencia,
      mesCargaTareas:
        normalizarMes(plan.mesCargaTareas_default) || prev.mesCargaTareas,
      mesFacturacion:
        normalizarMes(plan.mesFacturacion_default) || prev.mesFacturacion,
      // Si el servicio tiene encargado por defecto, pre-llenar el responsable
      refEmpleado: plan.empleado_encargado_default || prev.refEmpleado,
    }));
    if (plan.nombre) campos.push("faseTitulo");
    if (plan.descripcion_publica || plan.descripcion)
      campos.push("faseObjetivo");
    if (plan.alcance) campos.push("faseActividades");
    if (plan.condiciones) campos.push("faseEntregable");
    if (plan.valor != null) campos.push("valorOriginal");
    if (plan.tipoMoneda) campos.push("tipoMoneda");
    marcarCatalogo(campos);
  };

  const handleSeleccionarTarea = (_, tarea) => {
    if (!tarea) {
      setLocal((prev) => ({ ...prev, refTipoTarea: "" }));
      setCamposCatalogo(new Set());
      return;
    }
    const campos = [];
    const valorOriginal = tarea.valor ?? 0;

    setLocal((prev) => ({
      ...prev,
      refTipoTarea: tarea.nombre,
      nombreServicio: tarea.nombre,
      faseTitulo: tarea.nombre || prev.faseTitulo,
      faseObjetivo:
        tarea.objetivo ||
        tarea.descripcion_publica ||
        tarea.descripcion ||
        prev.faseObjetivo,
      faseActividades: tarea.alcance || prev.faseActividades,
      faseEntregable: tarea.entregable || prev.faseEntregable,
      faseDuracion: tarea.duracion_estimada || prev.faseDuracion,
      valorOriginal,
      valorConDescuento: calcValorConDescuento(valorOriginal, prev.descuento),
      tipoMoneda: normalizarMoneda(tarea.tipoMoneda) || prev.tipoMoneda,
      // Si el servicio tiene encargado por defecto, pre-llenar el responsable
      refEmpleado: tarea.empleado_encargado_default || prev.refEmpleado,
    }));
    if (tarea.nombre) campos.push("faseTitulo");
    if (tarea.objetivo || tarea.descripcion_publica || tarea.descripcion)
      campos.push("faseObjetivo");
    if (tarea.alcance) campos.push("faseActividades");
    if (tarea.entregable) campos.push("faseEntregable");
    if (tarea.duracion_estimada) campos.push("faseDuracion");
    if (tarea.valor != null) campos.push("valorOriginal");
    if (tarea.tipoMoneda) campos.push("tipoMoneda");
    marcarCatalogo(campos);
  };

  // ── Props para campos narrativos con indicador de catálogo ──────────────
  const narrativaProps = (campo) => {
    const desdeCatalogo = camposCatalogo.has(campo);
    return {
      sx: desdeCatalogo
        ? { "& .MuiOutlinedInput-root": { backgroundColor: CATALOG_BG } }
        : {},
      InputProps: desdeCatalogo
        ? {
            endAdornment: (
              <Tooltip title="Pre-rellenado desde catálogo — edita libremente">
                <Box sx={{ display: "flex", alignItems: "center", pr: 0.5 }}>
                  <Iconify
                    icon={bookOpenFill}
                    width={16}
                    height={16}
                    style={{ color: "#1565C0" }}
                  />
                </Box>
              </Tooltip>
            ),
          }
        : undefined,
      onChange: (e) => {
        if (desdeCatalogo) desmarcarCatalogo(campo);
        set(campo, e.target.value);
      },
    };
  };

  // ── Handlers de valor con cálculo automático de descuento ───────────────
  const handleValorOriginalChange = (e) => {
    const v = e.target.value;
    setLocal((prev) => ({
      ...prev,
      valorOriginal: v,
      valorConDescuento: calcValorConDescuento(v, prev.descuento),
    }));
  };

  const handleDescuentoChange = (e) => {
    const d = e.target.value;
    setLocal((prev) => ({
      ...prev,
      descuento: d,
      valorConDescuento: calcValorConDescuento(prev.valorOriginal, d),
    }));
  };

  const tieneDescuento = Number(local.descuento) > 0;

  // ── Clientes ─────────────────────────────────────────────────────────────
  const dispatch = useDispatch();
  const { data: clientes = [] } =
    clientesApi.useGetClientesQuery(CLIENTES_QUERY_ARGS);
  const clienteActual =
    clientes.find((c) => c.rut === local.refCliente) || null;

  const [mostrarFormCliente, setMostrarFormCliente] = useState(false);
  const [formCliente, setFormCliente] = useState(CLIENTE_NUEVO_INICIAL);
  const [alertaRutCliente, setAlertaRutCliente] = useState(false);
  const [addCliente, { isLoading: isCreandoCliente }] =
    clientesApi.useAddClienteMutation();

  const handleCampoCliente = (campo) => (e) => {
    setFormCliente((prev) => ({ ...prev, [campo]: e.target.value }));
    if (campo === "rut") setAlertaRutCliente(false);
  };

  const confirmarClienteNuevo = async () => {
    if (!formCliente.nombre.trim()) {
      enqueueSnackbar("El nombre del cliente es requerido.", {
        variant: "warning",
      });
      return;
    }
    if (!formCliente.correo.trim()) {
      enqueueSnackbar("El correo del cliente es requerido.", {
        variant: "warning",
      });
      return;
    }
    if (!formCliente.telefono.trim()) {
      enqueueSnackbar("El teléfono es requerido.", { variant: "warning" });
      return;
    }
    if (!formCliente.direccion.trim()) {
      enqueueSnackbar("La dirección es requerida.", { variant: "warning" });
      return;
    }
    if (!formCliente.comuna.trim()) {
      enqueueSnackbar("La comuna es requerida.", { variant: "warning" });
      return;
    }

    const rutIngresado = formCliente.rut.trim();

    if (rutIngresado) {
      const rutNormalizado = normalizarRut(rutIngresado);
      const existente = clientes.find(
        (c) => normalizarRut(c.rut) === rutNormalizado,
      );
      if (existente) {
        setAlertaRutCliente(true);
        return;
      }
    }

    // El identificador provisorio se genera sin guion: el backend lo quitaría al
    // guardar y el formulario quedaría referenciando un rut inexistente.
    const rut = rutIngresado
      ? normalizarRut(rutIngresado)
      : `PROV${Date.now()}`;
    const esRutProvisorio = rutIngresado ? 0 : 1;
    const datosCliente = {
      nombre: formCliente.nombre.trim(),
      rut,
      correo: formCliente.correo.trim(),
      telefono: formCliente.telefono.trim(),
      direccion: formCliente.direccion.trim(),
      comuna: formCliente.comuna.trim(),
      contacto: "",
      comentarios: "",
    };

    try {
      const clienteCreado = await addCliente({
        ...datosCliente,
        esRutProvisorio,
        estado: "activo",
        portalActivo: 0,
        contrasena: "",
        correoFacturacion: "",
      }).unwrap();

      // Manda el registro que devuelve el backend. El respaldo solo cubre una
      // respuesta vacía y declara lo que el backend guarda tal como se envió,
      // más el estado ACTIVO que la creación fija por su cuenta. Otros pasos del
      // formulario leen esta misma lista cacheada, así que un respaldo con menos
      // campos los dejaría viendo un cliente a medias.
      const clienteNuevo = clienteCreado?.rut
        ? clienteCreado
        : { ...datosCliente, estado: "ACTIVO" };

      // El valor del selector se deriva de la lista cacheada: sin esta escritura
      // el cliente recién creado no está en ella y el selector se ve vacío hasta
      // que llega la recarga. El refetch que dispara la invalidación reemplaza
      // después esta entrada con la versión del servidor.
      dispatch(
        clientesApi.util.updateQueryData(
          "getClientes",
          CLIENTES_QUERY_ARGS,
          (draft) => {
            if (!draft.some((c) => c.rut === clienteNuevo.rut)) {
              draft.push(clienteNuevo);
            }
          },
        ),
      );

      set("refCliente", clienteNuevo.rut);
      set("nombreCliente", clienteNuevo.nombre);
      setMostrarFormCliente(false);
      setFormCliente(CLIENTE_NUEVO_INICIAL);
      enqueueSnackbar("Cliente creado exitosamente.", { variant: "success" });
    } catch {
      enqueueSnackbar("Error al crear el cliente.", { variant: "error" });
    }
  };

  const planSeleccionado =
    planesActivos.find((p) => p.nombre === local.refTipoPlan) || null;
  const tareaSeleccionada =
    tareasActivas.find((t) => t.nombre === local.refTipoTarea) || null;

  // ── Derivacion por defecto del servicio elegido ──────────────────────────
  // El servicio del catalogo puede traer un encargado/sucursal por defecto y,
  // opcionalmente, exigir que la asignacion no se pueda cambiar manualmente.
  const servicioSeleccionado =
    local.tipoItem === "PLAN" ? planSeleccionado : tareaSeleccionada;
  const tieneEncargadoDefault =
    !!servicioSeleccionado?.empleado_encargado_default;
  const bloquearAsignacion =
    tieneEncargadoDefault && !!servicioSeleccionado?.bloquear_asignacion;
  const sucursalDefault = servicioSeleccionado?.sucursal_default || "";
  // Resolver el nombre del encargado con la lista ya disponible (sin fetch).
  const encargadoDefault = tieneEncargadoDefault
    ? empleados.find(
        (e) => e.rut === servicioSeleccionado.empleado_encargado_default,
      )
    : null;
  const nombreEncargadoDefault = encargadoDefault
    ? `${encargadoDefault.nombre} ${encargadoDefault.apellidos}`.trim()
    : "";

  const handleGuardar = () => {
    // El backend exige nombreServicio (no vacío) para todo ítem. Para TAREA sin
    // servicio de catálogo el campo no se autocompleta, así que validamos aquí
    // antes de guardar y evitamos el 400 críptico al enviar la propuesta.
    const erroresLocal = {};
    if (!local.nombreServicio?.trim()) {
      erroresLocal.nombreServicio =
        "El nombre del servicio es obligatorio para guardar.";
    }
    if (Object.keys(erroresLocal).length > 0) {
      setErrores(erroresLocal);
      setTab(1); // Pestaña Operacional, donde vive el campo
      enqueueSnackbar(
        "Completa el nombre del servicio en la pestaña Operacional.",
        { variant: "warning" },
      );
      return;
    }
    onSave(local);
    // No llamar onClose() aquí — el padre cierra el dialog desde su handleGuardar
    // para evitar que handleCerrar vea isNew=true (batch de estado) y elimine el ítem
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h6">
                {isNew ? "Agregar fase / servicio" : "Editar fase / servicio"}
              </Typography>
              <Chip
                label={
                  local.tipoItem === "PLAN"
                    ? "PLAN recurrente"
                    : "TAREA especial"
                }
                size="small"
                sx={{
                  bgcolor:
                    local.tipoItem === "PLAN"
                      ? "#E3F2FD"
                      : BRAND.color.primaryBg,
                  color:
                    local.tipoItem === "PLAN" ? "#1565C0" : BRAND.color.primary,
                  fontWeight: 600,
                  mt: 0.5,
                }}
              />
            </Box>
            <IconButton size="small" onClick={onClose}>
              <Iconify icon={closeFill} width={20} height={20} />
            </IconButton>
          </Box>
        </DialogTitle>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ px: 3, borderBottom: "1px solid", borderColor: "divider" }}
        >
          <Tab label="Contenido del PDF" />
          <Tab label="Datos operacionales" />
        </Tabs>

        <DialogContent sx={{ pt: 2 }}>
          {/* ═══════════════════════════════ TAB 0 — PDF (narrativa) ═════════ */}
          {tab === 0 && (
            <Grid container spacing={2}>
              {/* Tipo */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo *</InputLabel>
                  <Select
                    value={local.tipoItem}
                    onChange={(e) => {
                      set("tipoItem", e.target.value);
                      set("refTipoPlan", "");
                      set("refTipoTarea", "");
                      setCamposCatalogo(new Set());
                    }}
                    label="Tipo *"
                  >
                    <MenuItem value="PLAN">PLAN (recurrente)</MenuItem>
                    <MenuItem value="TAREA">TAREA (especial)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Selector catálogo */}
              {local.tipoItem === "PLAN" && (
                <Grid item xs={12} sm={8} id={`item-${itemIndex}-refTipoPlan`}>
                  <Autocomplete
                    options={planesActivos}
                    getOptionLabel={(p) => p.nombre || ""}
                    value={planSeleccionado}
                    onChange={handleSeleccionarPlan}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Servicio del catálogo *"
                        helperText="Obligatorio para ítems PLAN. Pre-rellena el contenido del PDF."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.nombre}>
                        <Box>
                          <Typography variant="body2">
                            {option.nombre}
                          </Typography>
                          {option.categoria && (
                            <Typography variant="caption" color="textSecondary">
                              {option.categoria}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
              )}
              {local.tipoItem === "TAREA" && (
                <Grid item xs={12} sm={8}>
                  <Autocomplete
                    options={tareasActivas}
                    getOptionLabel={(t) => t.nombre || ""}
                    value={tareaSeleccionada}
                    onChange={handleSeleccionarTarea}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Servicio del catálogo (opcional)"
                        helperText="Opcional — pre-rellena objetivo, actividades y entregable."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.nombre}>
                        <Box>
                          <Typography variant="body2">
                            {option.nombre}
                          </Typography>
                          {option.categoria && (
                            <Typography variant="caption" color="textSecondary">
                              {option.categoria}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
              )}

              {camposCatalogo.size > 0 && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "#1565C0",
                    }}
                  >
                    <Iconify icon={bookOpenFill} width={14} height={14} />
                    <Typography variant="caption">
                      Los campos con fondo azul fueron prellenados desde el
                      catálogo. Edítalos libremente.
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Título */}
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Título de la fase"
                  value={local.faseTitulo}
                  {...narrativaProps("faseTitulo")}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <VoiceInputButton
                    onTranscript={(t) => {
                      desmarcarCatalogo("faseTitulo");
                      set(
                        "faseTitulo",
                        local.faseTitulo ? `${local.faseTitulo} ${t}` : t,
                      );
                    }}
                  />
                  <AICorrectButton
                    text={local.faseTitulo}
                    context="Título de una fase o servicio en una propuesta comercial tributaria. Debe ser breve y descriptivo."
                    onCorrected={(t) => {
                      desmarcarCatalogo("faseTitulo");
                      set("faseTitulo", t);
                    }}
                    disabled={!local.faseTitulo}
                  />
                </Box>
              </Grid>

              {/* Duración — solo visible para TAREA o si el plan ya tiene valor */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label={
                    local.tipoItem === "PLAN"
                      ? "Duración (opcional)"
                      : "Duración estimada"
                  }
                  placeholder={
                    local.tipoItem === "PLAN"
                      ? "Ej: 6 meses (si aplica)"
                      : "Ej: 3 semanas"
                  }
                  value={local.faseDuracion}
                  helperText={
                    local.tipoItem === "PLAN"
                      ? "Plazo de este servicio. Para planes recurrentes sin fin, déjalo vacío."
                      : "Plazo específico de este servicio. La duración global del proyecto se setea en el paso Información Adicional."
                  }
                  {...narrativaProps("faseDuracion")}
                />
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}
                >
                  <VoiceInputButton
                    onTranscript={(t) => {
                      desmarcarCatalogo("faseDuracion");
                      set(
                        "faseDuracion",
                        local.faseDuracion ? `${local.faseDuracion} ${t}` : t,
                      );
                    }}
                  />
                </Box>
              </Grid>

              {/* Objetivo */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Objetivo del servicio"
                  placeholder="¿Qué problema resuelve este servicio?"
                  value={local.faseObjetivo}
                  {...narrativaProps("faseObjetivo")}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <VoiceInputButton
                    onTranscript={(t) => {
                      desmarcarCatalogo("faseObjetivo");
                      set(
                        "faseObjetivo",
                        local.faseObjetivo ? `${local.faseObjetivo} ${t}` : t,
                      );
                    }}
                  />
                  <AICorrectButton
                    text={local.faseObjetivo}
                    context="Objetivo de un servicio en una propuesta comercial tributaria chilena. ¿Qué problema resuelve este servicio?"
                    onCorrected={(t) => {
                      desmarcarCatalogo("faseObjetivo");
                      set("faseObjetivo", t);
                    }}
                    disabled={!local.faseObjetivo}
                  />
                </Box>
              </Grid>

              {/* Actividades */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Actividades / Alcance"
                  placeholder={
                    "¿Qué actividades incluye? (una por línea → lista en el PDF)"
                  }
                  value={local.faseActividades}
                  {...narrativaProps("faseActividades")}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <VoiceInputButton
                    onTranscript={(t) => {
                      desmarcarCatalogo("faseActividades");
                      set(
                        "faseActividades",
                        local.faseActividades
                          ? `${local.faseActividades}\n${t}`
                          : t,
                      );
                    }}
                  />
                  <AICorrectButton
                    text={local.faseActividades}
                    context="Actividades o alcance de un servicio en una propuesta comercial tributaria chilena. Lista de actividades incluidas en el servicio."
                    onCorrected={(t) => {
                      desmarcarCatalogo("faseActividades");
                      set("faseActividades", t);
                    }}
                    disabled={!local.faseActividades}
                  />
                </Box>
              </Grid>

              {/* Entregable */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Entregable"
                  placeholder="¿Qué recibe el cliente al finalizar?"
                  value={local.faseEntregable}
                  {...narrativaProps("faseEntregable")}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <VoiceInputButton
                    onTranscript={(t) => {
                      desmarcarCatalogo("faseEntregable");
                      set(
                        "faseEntregable",
                        local.faseEntregable
                          ? `${local.faseEntregable} ${t}`
                          : t,
                      );
                    }}
                  />
                  <AICorrectButton
                    text={local.faseEntregable}
                    context="Entregable de un servicio en una propuesta comercial tributaria chilena. ¿Qué recibe el cliente al finalizar?"
                    onCorrected={(t) => {
                      desmarcarCatalogo("faseEntregable");
                      set("faseEntregable", t);
                    }}
                    disabled={!local.faseEntregable}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {/* ═══════════════════════════════ TAB 1 — OPERACIONAL ════════════ */}
          {tab === 1 && (
            <Grid container spacing={2}>
              {/* Nombre del servicio */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={
                    local.tipoItem === "PLAN"
                      ? "Servicio del catálogo"
                      : "Nombre del servicio *"
                  }
                  value={local.nombreServicio}
                  onChange={(e) => set("nombreServicio", e.target.value)}
                  disabled={local.tipoItem === "PLAN" && !!local.refTipoPlan}
                  error={!!errores.nombreServicio}
                  helperText={
                    errores.nombreServicio ||
                    (local.tipoItem === "PLAN"
                      ? local.refTipoPlan
                        ? "Identificador interno del servicio (vínculo al catálogo). Lo que verá el cliente se edita en el 'Título de la fase' de la pestaña 'Contenido del PDF'."
                        : "Selecciona un plan en la pestaña 'Contenido del PDF' para rellenar este campo."
                      : "Nombre del servicio para uso interno. El título que aparece en el PDF se edita en la pestaña 'Contenido del PDF'.")
                  }
                />
              </Grid>

              {/* ── Cliente ── */}
              <Grid item xs={12} id={`item-${itemIndex}-refCliente`}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Empresa cliente *
                </Typography>
                {!mostrarFormCliente && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={clientes}
                      getOptionLabel={(c) =>
                        c ? `${c.nombre} — ${c.rut}` : ""
                      }
                      value={clienteActual}
                      onChange={(_, c) => {
                        set("refCliente", c?.rut ?? "");
                        set("nombreCliente", c?.nombre ?? "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Seleccionar cliente" />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.rut}>
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2" component="span">
                                {option.nombre}
                              </Typography>
                              {option.esRutProvisorio === 1 && (
                                <Chip
                                  label="RUT provisorio"
                                  size="small"
                                  sx={{
                                    bgcolor: BRAND.color.primary,
                                    color: "#fff",
                                    height: 18,
                                    fontSize: 10,
                                  }}
                                />
                              )}
                              {option.estado === "INACTIVO" && (
                                <Chip
                                  label="Inactivo"
                                  size="small"
                                  sx={{ height: 18, fontSize: 10 }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                              {option.rut}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                    <Button
                      variant="outlined"
                      onClick={() => setMostrarFormCliente(true)}
                      sx={{ height: 56, whiteSpace: "nowrap" }}
                    >
                      Crear nuevo
                    </Button>
                  </Box>
                )}

                {!mostrarFormCliente &&
                  clienteActual?.estado === "INACTIVO" && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Este cliente está inactivo; se reactivará cuando acepte un
                      servicio.
                    </Typography>
                  )}

                {mostrarFormCliente && (
                  <Box
                    sx={{
                      p: 2,
                      border: "1px dashed",
                      borderColor: "primary.light",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Nuevo cliente
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Nombre *"
                          value={formCliente.nombre}
                          onChange={handleCampoCliente("nombre")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="RUT (opcional)"
                          value={formCliente.rut}
                          onChange={handleCampoCliente("rut")}
                          error={alertaRutCliente}
                          helperText={
                            alertaRutCliente
                              ? "Ya existe un cliente con ese RUT."
                              : ""
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Correo *"
                          type="email"
                          value={formCliente.correo}
                          onChange={handleCampoCliente("correo")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Teléfono *"
                          value={formCliente.telefono}
                          onChange={handleCampoCliente("telefono")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Dirección *"
                          value={formCliente.direccion}
                          onChange={handleCampoCliente("direccion")}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Comuna *"
                          value={formCliente.comuna}
                          onChange={handleCampoCliente("comuna")}
                        />
                      </Grid>
                    </Grid>
                    {!formCliente.rut.trim() && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Se asignará un RUT provisorio. Podrás actualizarlo en la
                        ficha del cliente.
                      </Alert>
                    )}
                    <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={confirmarClienteNuevo}
                        disabled={isCreandoCliente}
                        sx={{
                          bgcolor: BRAND.color.primary,
                          "&:hover": { bgcolor: BRAND.color.primaryDark },
                        }}
                      >
                        {isCreandoCliente ? "Creando..." : "Confirmar"}
                      </Button>
                      <Button
                        variant="text"
                        color="inherit"
                        onClick={() => {
                          setMostrarFormCliente(false);
                          setFormCliente(CLIENTE_NUEVO_INICIAL);
                        }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Responsable */}
              <Grid item xs={12} sm={6} id={`item-${itemIndex}-refEmpleado`}>
                <Autocomplete
                  options={empleados}
                  getOptionLabel={(e) =>
                    e ? `${e.nombre} ${e.apellidos}` : ""
                  }
                  value={
                    empleados.find((e) => e.rut === local.refEmpleado) || null
                  }
                  onChange={(_, emp) => set("refEmpleado", emp?.rut ?? "")}
                  disabled={!sucursal || bloquearAsignacion}
                  noOptionsText={
                    sucursal
                      ? "Sin empleados disponibles"
                      : "Selecciona una sucursal primero"
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Responsable *" />
                  )}
                />
                {tieneEncargadoDefault && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {bloquearAsignacion
                      ? `Este servicio se asignará${
                          sucursalDefault
                            ? ` a la sucursal ${sucursalDefault}`
                            : ""
                        } con ${
                          nombreEncargadoDefault
                            ? `el encargado ${nombreEncargadoDefault}`
                            : "el encargado predefinido"
                        } al aceptar la propuesta.`
                      : `Este servicio tiene un encargado sugerido${
                          nombreEncargadoDefault
                            ? ` (${nombreEncargadoDefault})`
                            : ""
                        }. Puedes cambiarlo si lo necesitas.`}
                  </Alert>
                )}
              </Grid>

              {/* Moneda */}
              <Grid item xs={12} sm={2}>
                <FormControl fullWidth>
                  <InputLabel>Moneda *</InputLabel>
                  <Select
                    value={local.tipoMoneda}
                    onChange={(e) => set("tipoMoneda", e.target.value)}
                    label="Moneda *"
                  >
                    {MONEDAS.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Valor original */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Valor original *"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0, step: 0.01 }}
                  value={local.valorOriginal}
                  onChange={handleValorOriginalChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {local.tipoMoneda}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Descuento */}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Descuento (%)"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0, max: 100 }}
                  value={local.descuento}
                  onChange={handleDescuentoChange}
                />
              </Grid>

              {/* Valor con descuento — solo editable si no hay descuento */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Valor final *"
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0, step: 0.01 }}
                  value={local.valorConDescuento}
                  onChange={(e) => {
                    if (!tieneDescuento)
                      set("valorConDescuento", e.target.value);
                  }}
                  InputProps={{
                    readOnly: tieneDescuento,
                    startAdornment: (
                      <InputAdornment position="start">
                        {local.tipoMoneda}
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    tieneDescuento ? "Calculado automáticamente" : undefined
                  }
                  sx={
                    tieneDescuento
                      ? { "& .MuiOutlinedInput-root": { bgcolor: "grey.50" } }
                      : {}
                  }
                />
              </Grid>

              {/* ── Campos PLAN ──────────────────────────────────────────────── */}
              {local.tipoItem === "PLAN" && (
                <>
                  <Grid item xs={12}>
                    <Divider>
                      <Typography variant="caption" color="textSecondary">
                        Plan recurrente
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    id={`item-${itemIndex}-tipoFrecuencia`}
                  >
                    <FormControl fullWidth>
                      <InputLabel>Frecuencia *</InputLabel>
                      <Select
                        value={local.tipoFrecuencia}
                        onChange={(e) => {
                          const nuevo = e.target.value;
                          set("tipoFrecuencia", nuevo);
                          if (nuevo === "MES") set("mesCargaTareas", "");
                        }}
                        label="Frecuencia *"
                      >
                        <MenuItem value="MES">Mensual</MenuItem>
                        <MenuItem value="AÑO">Anual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    id={`item-${itemIndex}-fechaSiguienteCiclo`}
                  >
                    <DatePicker
                      label="Fecha siguiente ciclo"
                      value={strToDate(local.fechaSiguienteCiclo)}
                      onChange={(date) =>
                        set("fechaSiguienteCiclo", dateToStr(date))
                      }
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>

                  {/* Fecha término descuento: solo si hay descuento */}
                  {tieneDescuento && (
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Fecha término descuento"
                        value={strToDate(local.fechaTerminoDesc)}
                        onChange={(date) =>
                          set("fechaTerminoDesc", dateToStr(date))
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>
                  )}

                  {local.tipoFrecuencia === "AÑO" && (
                    <>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        id={`item-${itemIndex}-mesCargaTareas`}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Mes carga de tareas</InputLabel>
                          <Select
                            value={local.mesCargaTareas}
                            onChange={(e) =>
                              set("mesCargaTareas", e.target.value)
                            }
                            label="Mes carga de tareas"
                          >
                            <MenuItem value="">— Sin definir —</MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m.value} value={m.value}>
                                {m.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        id={`item-${itemIndex}-mesFacturacion`}
                      >
                        <FormControl fullWidth>
                          <InputLabel>Mes facturación</InputLabel>
                          <Select
                            value={local.mesFacturacion}
                            onChange={(e) =>
                              set("mesFacturacion", e.target.value)
                            }
                            label="Mes facturación"
                          >
                            <MenuItem value="">— Sin definir —</MenuItem>
                            {MESES.map((m) => (
                              <MenuItem key={m.value} value={m.value}>
                                {m.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={local.seFacturaGestor}
                          onChange={(e) =>
                            set("seFacturaGestor", e.target.checked)
                          }
                        />
                      }
                      label="Se factura a través del gestor"
                    />
                  </Grid>
                </>
              )}

              {/* ── Campos TAREA ─────────────────────────────────────────────── */}
              {local.tipoItem === "TAREA" && (
                <>
                  <Grid item xs={12}>
                    <Divider>
                      <Typography variant="caption" color="textSecondary">
                        Tarea especial
                      </Typography>
                    </Divider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Fecha límite"
                      value={strToDate(local.fechaLimite)}
                      onChange={(date) => set("fechaLimite", dateToStr(date))}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                    />
                  </Grid>
                  {tieneDescuento && (
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Fecha término descuento"
                        value={strToDate(local.fechaTerminoDesc)}
                        onChange={(date) =>
                          set("fechaTerminoDesc", dateToStr(date))
                        }
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={local.vendidoTaxtic}
                          onChange={(e) =>
                            set("vendidoTaxtic", e.target.checked)
                          }
                        />
                      }
                      label="Vendido por Taxtic"
                    />
                  </Grid>
                </>
              )}

              {/* Observaciones */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Observaciones internas"
                  multiline
                  minRows={2}
                  value={local.observaciones}
                  onChange={(e) => set("observaciones", e.target.value)}
                  helperText="Solo visible internamente, no aparece en el PDF."
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 0.5,
                    mt: 0.5,
                  }}
                >
                  <VoiceInputButton
                    onTranscript={(t) =>
                      set(
                        "observaciones",
                        local.observaciones ? `${local.observaciones} ${t}` : t,
                      )
                    }
                  />
                  <AICorrectButton
                    text={local.observaciones}
                    context="Observaciones internas de un ítem de propuesta comercial tributaria."
                    onCorrected={(t) => set("observaciones", t)}
                    disabled={!local.observaciones}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!isNew && onDelete && (
            <Tooltip title="Eliminar esta fase">
              <IconButton
                size="small"
                onClick={onDelete}
                sx={{ color: "error.main", mr: "auto" }}
              >
                <Iconify icon={trash2Fill} width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
          <Button variant="text" color="inherit" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardar}
            sx={{
              bgcolor: BRAND.color.primary,
              "&:hover": { bgcolor: BRAND.color.primaryDark },
            }}
          >
            {isNew ? "Agregar fase" : "Guardar cambios"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

FaseEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  itemIndex: PropTypes.number.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  empleados: PropTypes.array.isRequired,
  sucursal: PropTypes.string,
  isNew: PropTypes.bool,
};

FaseEditDialog.defaultProps = {
  sucursal: "",
  isNew: false,
};

export default FaseEditDialog;
